
// src/modules/clients/pages/ClientsListPage.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import { Add, Search, Person } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

type Client = {
  _id: string;
  fullName?: string;
  email?: string;
  phone?: string;
  loyaltyPoints?: number;
};

export default function ClientsListPage() {
  useProtectedRoute();
  const { token } = useAuth();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(false);

  const heroGradient = useMemo(
    () =>
      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 55%, #0f172a 100%)`,
    [theme]
  );
  const surfaceBorder = `1px solid ${theme.palette.mode === "light" ? "#e5e7eb" : theme.palette.divider}`;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let mounted = true;
    const fetchClients = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/clients?search=${encodeURIComponent(debounced)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;
        setClients(res.data || []);
      } catch {
        if (!mounted) return;
        setClients([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchClients();
    return () => {
      mounted = false;
    };
  }, [debounced, token]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 1, md: 2 } }}>
      {/* HERO / HEADER */}
      <Card
        component={motion.div}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: surfaceBorder,
          background: heroGradient,
          color: "white",
        }}
      >
        <CardContent sx={{ py: { xs: 2.25, md: 2.75 } }}>
          <Grid container alignItems="center" spacing={2} justifyContent="space-between">
            <Grid item xs={12} md="auto">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    width: 44,
                    height: 44,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant={isSmDown ? "h5" : "h4"} sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                    Clients
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Manage customers, contact details, and loyalty points.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.25,
                  flexWrap: "wrap",
                  justifyContent: { xs: "stretch", md: "flex-end" },
                  mt: { xs: 1, md: 0 },
                }}
              >
                <TextField
                  placeholder="Search by name, email, or phone…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  fullWidth={isSmDown}
                  sx={{
                    minWidth: { xs: "100%", md: 340 },
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "rgba(255,255,255,0.92)",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "transparent" },
                      "&.Mui-focused fieldset": { borderColor: "transparent" },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Add />}
                  onClick={() => navigate("/clients/add")}
                  sx={{
                    fontWeight: 800,
                    px: 2.2,
                    boxShadow: "none",
                    "&:hover": { boxShadow: 2 },
                  }}
                >
                  Add New Client
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* CONTENT CARD */}
      <Card
        component={motion.div}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.05 }}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: surfaceBorder,
          overflow: "hidden",
        }}
      >
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: { md: 560 }, overflow: "auto" }}>
          <Table stickyHeader size="medium" aria-label="clients-table">
            <TableHead>
              <TableRow sx={{ "& th": { backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "action.hover" } }}>
                <TableCell sx={{ fontWeight: 800 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Loyalty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell colSpan={5} sx={{ py: 1.25 }}>
                      <Skeleton height={28} />
                    </TableCell>
                  </TableRow>
                ))
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box
                      sx={{
                        py: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.mode === "light" ? "#eef2ff" : "primary.dark",
                          color: "primary.main",
                          width: 56,
                          height: 56,
                        }}
                      >
                        <Person />
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 0.5 }}>
                        No clients found
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Try a different search or add a new client.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate("/clients/add")}
                        sx={{ fontWeight: 800 }}
                      >
                        Add Client
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => {
                  const lp = client.loyaltyPoints ?? 0;
                  return (
                    <TableRow
                      key={client._id}
                      hover
                      sx={{
                        "&:hover": {
                          background:
                            theme.palette.mode === "light"
                              ? "linear-gradient(0deg, rgba(37,99,235,0.05), rgba(37,99,235,0.05))"
                              : "rgba(37,99,235,0.08)",
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
                            {(client.fullName || "?").slice(0, 1).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 700, cursor: "pointer" }}
                              noWrap
                              onClick={() => navigate(`/clients/${client._id}/profile`)}
                              title="View profile"
                            >
                              {client.fullName || "Unnamed"}
                            </Typography>
                            {(client.email || client.phone) && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {client.email || client.phone}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>{client.email || <Typography color="text.disabled">—</Typography>}</TableCell>
                      <TableCell>{client.phone || <Typography color="text.disabled">—</Typography>}</TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={`${lp.toLocaleString()} pts`}
                          color={lp > 0 ? "success" : "default"}
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>

                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => navigate(`/clients/${client._id}/profile`)}
                          sx={{ fontWeight: 800 }}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          color="secondary"
                          variant="text"
                          onClick={() => navigate(`/clients/${client._id}/edit`)}
                          sx={{ fontWeight: 800 }}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Space before fixed footer seam */}
      <Box sx={{ height: 12 }} />
    </Box>
  );
}

